import { retailPlanningTheme } from '@bestseller/retail-planning.utils.retail-planning-theme';
import { ShortcutDrawer } from '@bestseller-bit/frontend-community.components.shortcut-drawer';
import { KeyboardShortcutsRegistryProvider } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Box, GlobalStyles, styled, ThemeProvider } from '@mui/material';
import { LicenseManager } from 'ag-grid-enterprise';
import MathJaxContext from 'better-react-mathjax/MathJaxContext';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { SnackbarProvider } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppContent } from './AppContent';
import { AuthenticateGuard } from './components/organisms/AuthenticateGuard/AuthenticateGuard';
import { DataDogRumProvider } from './contexts/DataDogRumContext';
import { DrawerContextProvider } from './contexts/DrawerContextProvider';
import { FilterContextProvider } from './contexts/FilterContext';
import { MockProvider } from './contexts/MockContext';
import { StoreCacheContextProvider } from './contexts/StoreCacheContext';
import { EnvironmentVariableError } from './domain/errors/EnvironmentVariable.error';
import { DevToolsProvider } from './hooks/devtools/DevToolsContext';
import { IsHistoricalProvider } from './hooks/historical/IsHistoricalContext';
import { useNotificationHub } from './hooks/notifications/useNotificationHub';
import { useIsDevEnv } from './hooks/useIsDevEnv';
import Initializer from './Initializer';

const openDrawerKeyCombo = {
    code: 'KeyK',
    altKey: true,
    description: 'Toggle keyboard shortcuts drawer',
};

const agGridLicense = import.meta.env.VITE_APP_AG_GRID_LICENSE_KEY;

if (!agGridLicense) throw new EnvironmentVariableError(`AG Grid license key missing`);

LicenseManager.setLicenseKey(agGridLicense);

const mathJaxConfig = {
    loader: { load: ['[tex]/html'] },
    tex: {
        packages: { '[+]': ['html'] },
        inlineMath: [
            ['$', '$'],
            ['\\(', '\\)'],
        ],
        displayMath: [
            ['$$', '$$'],
            ['\\[', '\\]'],
        ],
    },
};

const App = () => {
    const isDevEnv = useIsDevEnv();

    // Create a LaunchDarkly provider depending on whether we're in a dev environment or not
    const LDProvider = useMemo(() => {
        return withLDProvider({
            clientSideID: isDevEnv ? '639b0ca462455c121534c7fe' : '639b0ca462455c121534c7ff',
        })(AppContent);
    }, [isDevEnv]);

    useEffect(() => {
        if (isDevEnv) {
            let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = '/storeforecast-icon-dev.svg';
        }
    }, [isDevEnv]);

    const [shortcutsDrawerOpen, setShortcutsDrawerOpen] = useState(false);

    return (
        <MockProvider>
            <DevToolsProvider>
                <DataDogRumProvider>
                    <BrowserRouter>
                        <ThemeProvider theme={retailPlanningTheme}>
                            <AuthenticateGuard>
                                <NotificationHubSubscriber />
                                <FilterContextProvider>
                                    <SnackbarProvider maxSnack={3}>
                                        <IsHistoricalProvider>
                                            <MathJaxContext src={'/mathjax/tex-chtml.js'} config={mathJaxConfig}>
                                                <DrawerContextProvider>
                                                    <StoreCacheContextProvider>
                                                        <Initializer>
                                                            <GlobalStyles
                                                                styles={{
                                                                    '*': {
                                                                        scrollbarWidth: 'thin',
                                                                    },
                                                                }}
                                                            />
                                                            <KeyboardShortcutsRegistryProvider>
                                                                <LDProvider />
                                                                <ShortcutDrawerWrapper>
                                                                    <ShortcutDrawer
                                                                        openDrawerKeyCombo={openDrawerKeyCombo}
                                                                        open={shortcutsDrawerOpen}
                                                                        onToggle={() =>
                                                                            setShortcutsDrawerOpen((prev) => !prev)
                                                                        }
                                                                    />
                                                                </ShortcutDrawerWrapper>
                                                            </KeyboardShortcutsRegistryProvider>
                                                        </Initializer>
                                                    </StoreCacheContextProvider>
                                                </DrawerContextProvider>
                                            </MathJaxContext>
                                        </IsHistoricalProvider>
                                    </SnackbarProvider>
                                </FilterContextProvider>
                            </AuthenticateGuard>
                        </ThemeProvider>
                    </BrowserRouter>
                </DataDogRumProvider>
            </DevToolsProvider>
        </MockProvider>
    );
};

export default App;

const NotificationHubSubscriber = () => {
    useNotificationHub();
    return null;
};

const ShortcutDrawerWrapper = styled(Box)(({ theme }) => ({
    '.MuiDrawer-paper': {
        zIndex: theme.zIndex.drawer + 1,
    },
}));
